import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FILMSTRIP_CLOUD_MASK_A,
  FILMSTRIP_CLOUD_MASK_B,
  STORYBOARD_FILMSTRIP,
  filmstripSeamMask,
  filmstripSolidMask,
} from '@/nova/theme/tokens';
import { Cell, ClipFrame, Still, Strip } from './BeatFilmstrip.styles';

/** A box along the strip, in % of its width. */
export interface StripBox {
  left: number;
  width: number;
}

/** One frame of the strip: its cell on the timeline (its share before the overlaps), its drawn box (the cell plus the
 *  overlaps into its neighbours), whether its left edge is the strip's hard start, and its seam mask. In % of the strip. */
export interface FilmstripFrame {
  cell: StripBox;
  box: StripBox;
  hardLeft: boolean;
  mask: string;
}

/** One rendered beat's frames. */
export interface FilmstripSlot {
  frames: FilmstripFrame[];
}

/** A frame's drawn box for its cell: the cell plus SEAM_SHARE/2 of it on each side (none left of the hard start), so
 *  neighbours overlap where they cross-fade. */
export const frameBox = (cell: StripBox, hardLeft: boolean): StripBox => {
  const ext = (STORYBOARD_FILMSTRIP.SEAM_SHARE * cell.width) / 2;
  return { left: cell.left - (hardLeft ? 0 : ext), width: cell.width + ext * (hardLeft ? 1 : 2) };
};

/**
 * Lays out the strip: the first `done` beats (each sized by its length on the ad's timeline) with
 * STORYBOARD_FILMSTRIP.FRAMES_PER_BEAT frames each. Neighbouring frames overlap by SEAM_SHARE of a frame, across beats
 * too, and each overlap is a soft cross-fade (both frames fade over it). The strip's left edge is hard; every frame's
 * right edge fades (the last one into the dot field). Pure, so the geometry is testable.
 */
export const filmstripLayout = (durations: readonly number[], done: number): FilmstripSlot[] => {
  const total = durations.reduce((sum, d) => sum + Math.max(0, d), 0);
  if (total <= 0) return [];
  const n = STORYBOARD_FILMSTRIP.FRAMES_PER_BEAT;
  const seam = STORYBOARD_FILMSTRIP.SEAM_SHARE;
  const out: FilmstripSlot[] = [];
  let start = 0;
  for (let i = 0; i < Math.min(done, durations.length); i += 1) {
    const span = (Math.max(0, durations[i]) / total) * 100; // the beat's share of the strip, in %
    const base = span / n; // one frame's cell
    const frames = Array.from({ length: n }, (_, k) => {
      const hardLeft = i === 0 && k === 0;
      const cell = { left: start + k * base, width: base };
      const box = frameBox(cell, hardLeft);
      return { cell, box, hardLeft, mask: filmstripSeamMask(!hardLeft, true, (seam * base * 100) / box.width) };
    });
    out.push({ frames });
    start += span;
  }
  return out;
};

/**
 * Where the strip's edge is, in % of its width: the right end of the last revealed frame (shown[i] of beat i's frames
 * are revealed). The edge follows the frames as they land, a quarter of a beat at a time. Pure.
 */
export const edgeShare = (durations: readonly number[], shown: readonly number[]): number => {
  const total = durations.reduce((sum, d) => sum + Math.max(0, d), 0);
  if (total <= 0) return 0;
  const n = STORYBOARD_FILMSTRIP.FRAMES_PER_BEAT;
  let start = 0;
  let edge = 0;
  durations.forEach((d, i) => {
    const span = (Math.max(0, d) / total) * 100;
    const k = Math.min(n, shown[i] ?? 0);
    if (k > 0) edge = start + (span * k) / n;
    start += span;
  });
  return edge;
};

/**
 * The strip's mask for an edge at `edge`%: solid up to the cloud band, then the two noise layers over the band (the
 * last BAND% before the edge), scrolled `driftA` / `driftB` px down / up their tiles, so the strip's end dissolves into
 * the dot field as drifting cloud. Pure: the edge loop writes it every frame.
 */
export const cloudEdgeMask = (
  edge: number,
  driftA: number,
  driftB: number,
): { image: string; size: string; position: string } => {
  const band = STORYBOARD_FILMSTRIP.EDGE_BAND_PCT;
  const tile = STORYBOARD_FILMSTRIP.CLOUD_TILE_PX;
  const bandLeft = edge - band;
  const soft = bandLeft + band * 0.4;
  // A mask-position x of p% puts the layer's left at p% of (strip - layer): map the band's left (in % of the strip).
  const px = (bandLeft / (100 - band)) * 100;
  return {
    image: `${filmstripSolidMask(soft, soft + STORYBOARD_FILMSTRIP.EDGE_FADE_PCT)}, ${FILMSTRIP_CLOUD_MASK_A}, ${FILMSTRIP_CLOUD_MASK_B}`,
    size: `100% 100%, ${band}% ${tile}px, ${band}% ${tile}px`,
    position: `0 0, ${px}% ${driftA}px, ${px}% ${-driftB}px`,
  };
};

/**
 * How many beats have something to show: the contiguous run from beat 1 whose clip has rendered (segments[i + 1]) or
 * whose start still has been drawn (stills[i]: the opening for beat 1, the beat before's end frame after). Pure.
 */
export const presentBeats = (segments: readonly string[], stills: readonly string[], beats: number): number => {
  let done = 0;
  while (done < beats && (segments[done + 1] || stills[done])) done += 1;
  return done;
};

/**
 * Paints FRAMES_PER_BEAT frames of a beat's clip into canvases: a detached video decodes the clip and each seek (to the
 * middle of each quarter) paints one frame. The canvases are only displayed, never read back, so a clip served
 * without CORS headers still shows. Returns the canvases' refs and which ones are painted.
 */
const useClipFrames = (
  url: string,
): { canvases: React.MutableRefObject<(HTMLCanvasElement | null)[]>; drawn: boolean[] } => {
  const n = STORYBOARD_FILMSTRIP.FRAMES_PER_BEAT;
  const canvases = useRef<(HTMLCanvasElement | null)[]>([]);
  const [drawn, setDrawn] = useState<boolean[]>(() => Array.from({ length: n }, () => false));
  useEffect(() => {
    // Syncs with the browser's media decoder: a detached <video> for this clip, released on unmount / url change.
    // No clip yet (the beat still renders): nothing to decode.
    if (!url) return undefined;
    let cancelled = false;
    let next = 0;
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const seekTo = (k: number): void => {
      video.currentTime = (video.duration * (k + 0.5)) / n;
    };
    const onMeta = (): void => {
      if (!cancelled && Number.isFinite(video.duration) && video.duration > 0) seekTo(0);
    };
    const onSeeked = (): void => {
      if (cancelled) return;
      const canvas = canvases.current[next];
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (canvas && vw > 0 && vh > 0) {
        const k = Math.min(1, STORYBOARD_FILMSTRIP.CAPTURE_PX / Math.max(vw, vh));
        canvas.width = Math.round(vw * k);
        canvas.height = Math.round(vh * k);
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
        const painted = next;
        setDrawn((prev) => prev.map((d, i) => d || i === painted));
      }
      next += 1;
      if (next < n) seekTo(next);
    };
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('seeked', onSeeked);
    video.src = url;
    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('seeked', onSeeked);
      video.removeAttribute('src');
      video.load();
    };
  }, [url, n]);
  return { canvases, drawn };
};

/**
 * How many of a beat's frames are revealed after one more step: the next frame in order once it is painted, never one
 * ahead of an unpainted frame (so they always land left to right). Pure, for the reveal's timer.
 */
export const nextShown = (shown: number, drawn: readonly boolean[]): number =>
  shown < drawn.length && drawn[shown] ? shown + 1 : shown;

/**
 * Reveals a beat's painted frames one at a time, left to right, FRAME_STAGGER_MS apart (the first at once): painting
 * takes a few hundred ms for all four, and revealing them as they were painted read as one jump, not as progress.
 */
const useStaggeredReveal = (drawn: readonly boolean[], ready: boolean): number => {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    // Syncs with a timer: the next reveal step, rescheduled after each one while a painted frame waits (and only once
    // the beat before has landed all its frames, so the strip always grows left to right).
    if (!ready || nextShown(shown, drawn) === shown) return undefined;
    const id = window.setTimeout(
      () => setShown((s) => nextShown(s, drawn)),
      shown === 0 ? 0 : STORYBOARD_FILMSTRIP.FRAME_STAGGER_MS,
    );
    return () => window.clearTimeout(id);
  }, [shown, drawn, ready]);
  return shown;
};

interface BeatSlotProps {
  index: number;
  /** The beat's clip ("" until it renders). */
  url: string;
  /** The beat's drawn stills: where it starts (the opening, or the beat before's end) and where it ends ("" = not
   *  drawn yet). Its first half of cells holds the start, its second half the end, until the clip's frames replace them. */
  start: string;
  end: string;
  layout: FilmstripSlot;
  /** Reports how many of this beat's frames are revealed (the strip's edge follows them). */
  onShown: (index: number, shown: number) => void;
  /** The beat before has landed all its frames (beats several renders apart, or reused ones on a rerun, would
   *  otherwise reveal side by side at once). */
  ready: boolean;
}

/**
 * One beat: its cells land one at a time, left to right, as soon as each has something to show (its drawn still, so
 * the strip fills long before the beat's clip exists, or the clip's frame), and the clip's frames cross-fade in over
 * the stills once it renders.
 */
const BeatSlot = ({ index, url, start, end, layout, onShown, ready }: BeatSlotProps): React.ReactElement => {
  const { canvases, drawn } = useClipFrames(url);
  const half = STORYBOARD_FILMSTRIP.FRAMES_PER_BEAT / 2;
  // A still counts once it has loaded at least once (a later url swap, the drawn end replaced by the clip's real last
  // frame, keeps the cell up).
  const [startLoaded, setStartLoaded] = useState(false);
  const [endLoaded, setEndLoaded] = useState(false);
  const available = useMemo(
    () => drawn.map((d, k) => d || (k < half ? startLoaded : endLoaded)),
    [drawn, half, startLoaded, endLoaded],
  );
  const shown = useStaggeredReveal(available, ready);
  useEffect(() => onShown(index, shown), [index, shown, onShown]);
  return (
    <>
      {layout.frames.map((f, k) => {
        const still = k < half ? start : end;
        return (
          <Cell
            // The cells of a beat are fixed (FRAMES_PER_BEAT, never reordered), so the position is the identity.
            key={k}
            $shown={k < shown}
            style={{ left: `${f.box.left}%`, width: `${f.box.width}%`, maskImage: f.mask, WebkitMaskImage: f.mask }}
          >
            {still ? (
              <Still
                src={still}
                alt=""
                // One still spans its half's cells as one continuous picture (each cell shows its own slice), rather
                // than repeating the same crop side by side.
                style={{ width: `${half * 100}%`, left: `${-(k % half) * 100}%` }}
                onLoad={() => (k < half ? setStartLoaded(true) : setEndLoaded(true))}
              />
            ) : null}
            <ClipFrame
              ref={(el) => {
                canvases.current[k] = el;
              }}
              $painted={drawn[k]}
            />
          </Cell>
        );
      })}
    </>
  );
};

export interface BeatFilmstripProps {
  /** Index-aligned clip urls: [0] is the key visual's (empty) slot, [i] beat i's clip ("" until it renders). */
  segments: readonly string[];
  /** Index-aligned drawn frames: [0] the opening, [i] beat i's end frame (each drawn well before its clip renders). */
  stills: readonly string[];
  /** Each beat's length in seconds, in order: sizes its share of the strip. */
  durations: readonly number[];
}

/**
 * The run preview's filmstrip: each beat's share of the preview (sized by its length) fills with four cells, side by side
 * with soft cross-faded seams: its drawn start and end frames as soon as they exist, then its clip's frames once it
 * renders, so the preview fills left to right like a progress bar over the dot field from the first drawn frame on.
 */
export const BeatFilmstrip = ({ segments, stills, durations }: BeatFilmstripProps): React.ReactElement | null => {
  const done = presentBeats(segments, stills, durations.length);
  const layout = useMemo(() => filmstripLayout(durations, done), [durations, done]);
  const [shown, setShown] = useState<number[]>([]);
  const onShown = useCallback((index: number, count: number): void => {
    setShown((prev) => {
      if (prev[index] === count) return prev;
      const next = [...prev];
      next[index] = count;
      return next;
    });
  }, []);
  const target = edgeShare(durations, shown);
  const targetRef = useRef(target);
  targetRef.current = target;
  const stripRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Syncs with the display's frame clock: eases the edge toward the last revealed frame and drifts the cloud
    // layers, writing the strip's mask directly (no React render per frame). Reduced motion: no drift, no glide.
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const tile = STORYBOARD_FILMSTRIP.CLOUD_TILE_PX;
    let edge = targetRef.current;
    let raf = 0;
    const tick = (now: number): void => {
      const el = stripRef.current;
      edge = still ? targetRef.current : edge + (targetRef.current - edge) * STORYBOARD_FILMSTRIP.EDGE_EASE;
      if (el) {
        const a = still ? 0 : ((now / STORYBOARD_FILMSTRIP.CLOUD_A_LOOP_MS) * tile) % tile;
        const b = still ? 0 : ((now / STORYBOARD_FILMSTRIP.CLOUD_B_LOOP_MS) * tile) % tile;
        const m = cloudEdgeMask(edge, a, b);
        el.style.setProperty('mask-image', m.image);
        el.style.setProperty('-webkit-mask-image', m.image);
        el.style.setProperty('mask-size', m.size);
        el.style.setProperty('-webkit-mask-size', m.size);
        el.style.setProperty('mask-position', m.position);
        el.style.setProperty('-webkit-mask-position', m.position);
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, []);
  if (layout.length === 0) return null;
  return (
    <Strip ref={stripRef} aria-hidden="true">
      {layout.map((l, i) => (
        // One slot per beat index, kept when its clip arrives (its frames cross-fade in over the stills it showed).
        <BeatSlot
          key={i}
          index={i}
          url={segments[i + 1] ?? ''}
          start={stills[i] ?? ''}
          end={stills[i + 1] ?? ''}
          layout={l}
          onShown={onShown}
          ready={i === 0 || (shown[i - 1] ?? 0) >= STORYBOARD_FILMSTRIP.FRAMES_PER_BEAT}
        />
      ))}
    </Strip>
  );
};
